import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { Route, Switch } from 'react-router-dom';
import { Close, Dialog, IconButton, makeStyles, useMediaQuery, useTheme } from 'housecall-ui';
import HousecallThemeProvider from 'housecall-ui/dist/DesignSystem/Styles/HousecallThemeProvider';
import { HCPTheme } from 'housecall-ui/dist/Theme/Theme';
import CssBaseline from 'housecall-ui/dist/utils/CssBaseline';
import { QueryClientProvider } from 'resources/ReactQuery/QueryClientProvider';

import { Codeowners } from 'components/Common/Error/Codeowners';
import ErrorBoundary from 'components/Common/Error/ErrorBoundary';
import BrowserRouter from 'components/Common/Routing/BrowserRouter';

import { OnlineBookingStateProvider } from './state/OnlineBookingStateProvider';
import { HomeOwnerFlowContentWrapper } from './HomeOwnerFlowContentWrapper';

const useStyles = makeStyles((theme: HCPTheme) => ({
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
}));

const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
};

const HomeOwnerFlowWidget = () => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { closeButton } = useStyles();

  const [isHidden, setIsHidden] = useState(isInIframe);

  const refreshWidget = useCallback(() => {
    setIsHidden(true);

    setTimeout(() => {
      setIsHidden(false);
    }, 0);
  }, []);

  const messageHandler = (ev: MessageEvent<unknown>) => {
    if (ev.data === 'hcp:open') {
      setIsHidden(false);
    }
  };

  useEffect(() => {
    window.addEventListener('message', messageHandler);
    return () => {
      window.removeEventListener('message', messageHandler);
    };
  }, []);

  useEffect(() => {
    window.parent.postMessage('hcp:iframe-loaded', '*');
  }, []);

  const handleClose = useCallback(() => {
    window.parent.postMessage('hcp:close', '*');
    refreshWidget();
  }, [refreshWidget]);

  return (
    <>
      <Helmet title="Online booking" />
      <CssBaseline />
      <HousecallThemeProvider>
        <QueryClientProvider>
          <BrowserRouter disableTracking>
            <Switch>
              <Route path={['/pro/online_booking/:orgUuid', '/pro/online_booking']}>
                <Dialog open fullScreen={fullScreen} onClose={handleClose} maxWidth="sm" fullWidth>
                  <IconButton className={closeButton} aria-label="close" onClick={handleClose}>
                    <Close size="large" color="inherit" />
                  </IconButton>
                  <ErrorBoundary codeowners={Codeowners.CUSTOMER_INTAKE}>
                    {!isHidden && (
                      <OnlineBookingStateProvider>
                        <HomeOwnerFlowContentWrapper />
                      </OnlineBookingStateProvider>
                    )}
                  </ErrorBoundary>
                </Dialog>
              </Route>
            </Switch>
          </BrowserRouter>
        </QueryClientProvider>
      </HousecallThemeProvider>
    </>
  );
};

export default HomeOwnerFlowWidget;
